import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/api/client';
import { today } from '@/utils/helpers';
import { Send, Package, DollarSign, CheckCircle, Truck, FileText } from 'lucide-react';

// The 10-step lifecycle we surface visually
const STEPS = [
  { key: 'received', label: 'Received', match: (rma) => !!rma },
  { key: 'inspected', label: 'Tested', match: (rma) => !!rma.tested_by_name || !!rma.test_date || !!rma.diagnosis_notes },
  { key: 'sent', label: 'Sent to Supplier', match: (rma) => !!rma.sent_to_supplier_date || ['Sent to Supplier', 'Awaiting Supplier Response', 'Replaced by Supplier', 'Credited by Supplier', 'Rejected by Supplier', 'Delivered to Customer', 'Closed'].includes(rma.status) || !!rma.supplier_rma_no },
  { key: 'received_back', label: 'Resolved by Supplier', match: (rma) => !!rma.received_from_supplier_date || !!rma.replacement_note_no || !!rma.credit_note_no || ['Replaced by Supplier', 'Credited by Supplier', 'Rejected by Supplier', 'Delivered to Customer', 'Closed'].includes(rma.status) },
  { key: 'delivered', label: 'Delivered / Closed', match: (rma) => ['Delivered to Customer', 'Closed'].includes(rma.status) },
];

const detectResolution = (rma) => {
  if (rma.resolution_type) return rma.resolution_type;
  if (rma.credit_note_no) return 'Credit';
  if (rma.serial_replacement || rma.replacement_note_no) return 'Replacement';
  return '';
};

export const SupplierRMAWorkflow = ({ rma, products, onRefresh }) => {
  const [modal, setModal] = useState(''); // 'send' | 'replace' | 'credit' | 'deliver'
  const [saving, setSaving] = useState(false);

  // Form state per modal
  const [sendForm, setSendForm] = useState({
    supplier_rma_no: rma?.supplier_rma_no || '',
    sent_date: today(),
    notes: '',
  });
  const [replaceForm, setReplaceForm] = useState({
    replacement_serial: '',
    replacement_product_id: rma?.product_id || '',
    replacement_product_name: '',
    replacement_brand: '',
    replacement_model: '',
    supplier_rma_no: rma?.supplier_rma_no || '',
    delivery_note_no: '',
    replacement_note_no: '',
    replacement_doc_date: today(),
    replacement_remarks: '',
    received_date: today(),
    notes: '',
    force_upgraded: false,
  });
  const [repairedForm, setRepairedForm] = useState({
    supplier_rma_no: rma?.supplier_rma_no || '',
    replacement_note_no: '',
    replacement_doc_date: today(),
    replacement_remarks: '',
    received_date: today(),
    notes: '',
  });
  const [creditForm, setCreditForm] = useState({
    credit_note_no: '',
    supplier_rma_no: rma?.supplier_rma_no || '',
    replacement_doc_date: today(),
    replacement_remarks: '',
    received_date: today(),
    notes: '',
  });
  const [rejectForm, setRejectForm] = useState({
    rejection_reason: '',
    supplier_rma_no: rma?.supplier_rma_no || '',
    replacement_doc_date: today(),
    replacement_remarks: '',
    received_date: today(),
  });
  const [deliverForm, setDeliverForm] = useState({ notes: '' });

  React.useEffect(() => {
    // Refresh local form defaults when rma changes
    setSendForm(s => ({ ...s, supplier_rma_no: rma?.supplier_rma_no || s.supplier_rma_no }));
    setReplaceForm(s => ({
      ...s,
      supplier_rma_no: rma?.supplier_rma_no || s.supplier_rma_no,
      replacement_product_id: s.replacement_product_id || rma?.product_id || '',
    }));
    setRepairedForm(s => ({ ...s, supplier_rma_no: rma?.supplier_rma_no || s.supplier_rma_no }));
    setCreditForm(s => ({ ...s, supplier_rma_no: rma?.supplier_rma_no || s.supplier_rma_no }));
    setRejectForm(s => ({ ...s, supplier_rma_no: rma?.supplier_rma_no || s.supplier_rma_no }));
  }, [rma?.supplier_rma_no, rma?.product_id]);

  if (!rma) return null;

  const resolution = detectResolution(rma);
  const stepsDone = STEPS.map(s => ({ ...s, done: s.match(rma) }));
  const activeIdx = stepsDone.findIndex(s => !s.done);

  const canSend = ['Received', 'Pending', 'DOA', 'Approved for Supplier RMA', 'Under Inspection'].includes(rma.status);
  const canReceive = ['Sent to Supplier', 'Awaiting Supplier Response', 'Under Inspection', 'Awaiting Replacement'].includes(rma.status);
  const canDeliver = ['Replaced by Supplier', 'Replaced', 'Repaired', 'Upgraded Replacement'].includes(rma.status) && rma.rma_type !== 'Internal';

  const call = async (fn, ok) => {
    setSaving(true);
    try {
      await fn();
      setModal('');
      onRefresh && onRefresh();
      if (ok) alert(ok);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Action failed');
    } finally { setSaving(false); }
  };

  const submitSend = () => call(
    () => api.rmaSendToSupplier(rma.id, sendForm),
    'Marked as Sent to Supplier'
  );
  const submitReplace = () => {
    if (!replaceForm.replacement_serial.trim()) { alert('Replacement serial is required'); return; }
    // If no product selected AND no manual name entered, block
    if (!replaceForm.replacement_product_id && !replaceForm.replacement_product_name.trim()) {
      alert('Select a replacement product from inventory OR enter the new product name.');
      return;
    }
    const payload = { ...replaceForm };
    if (replaceForm.force_upgraded) payload.resolution_type = 'Upgraded Replacement';
    return call(
      () => api.rmaReceiveReplacement(rma.id, payload),
      'Replacement recorded and added to inventory'
    );
  };
  const submitRepaired = () => call(
    () => api.rmaReceiveRepaired(rma.id, repairedForm),
    'Marked as repaired (same serial)'
  );
  const submitCredit = () => {
    if (!creditForm.credit_note_no.trim()) { alert('Credit Note number is required'); return; }
    return call(
      () => api.rmaReceiveCredit(rma.id, creditForm),
      'Credit note recorded'
    );
  };
  const submitReject = () => {
    if (!rejectForm.rejection_reason.trim()) { alert('Rejection reason is required'); return; }
    return call(
      () => api.rmaRejectWarranty(rma.id, rejectForm),
      'Warranty rejection recorded'
    );
  };
  const submitDeliver = () => call(
    () => api.rmaDeliverToCustomer(rma.id, deliverForm),
    'Delivered to customer — RMA closed'
  );

  return (
    <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4" data-testid="supplier-workflow-panel">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200 flex items-center gap-2">
          <Truck size={16} /> Supplier RMA Lifecycle
        </h3>
        {resolution && (
          <Badge variant={resolution === 'Credit' ? 'info' : resolution === 'Replacement' ? 'success' : 'default'}>
            Resolution: {resolution}
          </Badge>
        )}
      </div>

      {/* Step progress bar */}
      <div className="flex items-center gap-1 mb-4 overflow-x-auto" data-testid="supplier-workflow-steps">
        {stepsDone.map((s, idx) => {
          const isActive = idx === activeIdx;
          const isDone = s.done;
          return (
            <React.Fragment key={s.key}>
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap
                ${isDone ? 'bg-emerald-500 text-white' : isActive ? 'bg-indigo-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'}`}>
                <span className="w-4 h-4 rounded-full bg-white/20 text-[10px] flex items-center justify-center">{idx + 1}</span>
                {s.label}
              </div>
              {idx < stepsDone.length - 1 && (
                <div className={`h-0.5 flex-1 min-w-[8px] ${isDone ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-700'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Reference numbers panel */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
        <RefField label="Supplier RMA No." value={rma.supplier_rma_no} />
        <RefField label="Delivery Note" value={rma.delivery_note_no} />
        <RefField label="Replacement Note" value={rma.replacement_note_no} />
        <RefField label="Credit Note" value={rma.credit_note_no} />
        <RefField label="Sent Date" value={rma.sent_to_supplier_date} />
        <RefField label="Received Date" value={rma.received_from_supplier_date} />
        <RefField label="Document Date" value={rma.replacement_doc_date} />
        <RefField label="Received By" value={rma.received_by_name} />
        <RefField label="Replacement Serial" mono value={rma.serial_replacement} />
        <RefField label="Supplier" value={rma.supplier_name || rma.purchase_info?.supplier_name} />
        {rma.replacement_remarks && (
          <div className="col-span-2 md:col-span-4">
            <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Document Remarks</div>
            <div className="text-sm italic">{rma.replacement_remarks}</div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button size="sm" variant="secondary" onClick={() => setModal('send')} disabled={!canSend} data-testid="btn-send-to-supplier">
          <Send size={14} className="mr-1" /> Send to Supplier
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setModal('replace')} disabled={!canReceive} data-testid="btn-receive-replacement">
          <Package size={14} className="mr-1" /> Receive Replacement
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setModal('repaired')} disabled={!canReceive} data-testid="btn-receive-repaired">
          <FileText size={14} className="mr-1" /> Mark Repaired (Same Serial)
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setModal('credit')} disabled={!canReceive} data-testid="btn-receive-credit">
          <DollarSign size={14} className="mr-1" /> Receive Credit
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setModal('reject')} disabled={!canReceive} data-testid="btn-reject-warranty">
          Reject Warranty
        </Button>
        <Button size="sm" variant="primary" onClick={() => setModal('deliver')} disabled={!canDeliver} data-testid="btn-deliver-to-customer">
          <CheckCircle size={14} className="mr-1" /> Deliver &amp; Close
        </Button>
      </div>

      {/* Supplier history log */}
      {(rma.supplier_history || []).length > 0 && (
        <div className="border-t border-indigo-200 dark:border-indigo-800 pt-3">
          <div className="text-xs font-semibold text-indigo-900 dark:text-indigo-200 mb-2 flex items-center gap-1">
            <FileText size={12} /> Supplier Timeline
          </div>
          <ul className="space-y-1.5" data-testid="supplier-history">
            {rma.supplier_history.map((ev, i) => (
              <li key={i} className="text-[11px] flex items-start gap-2">
                <span className="text-[var(--text-muted)] shrink-0">{(ev.at || '').slice(0, 10)}</span>
                <span className="font-semibold">{ev.action}</span>
                {ev.by_name && <span className="text-[var(--text-muted)]">by {ev.by_name}</span>}
                {ev.supplier_rma_no && <span className="text-indigo-600 dark:text-indigo-300 font-mono">RMA {ev.supplier_rma_no}</span>}
                {ev.delivery_note_no && <span className="text-indigo-600 dark:text-indigo-300 font-mono">DN {ev.delivery_note_no}</span>}
                {ev.replacement_note_no && <span className="text-indigo-600 dark:text-indigo-300 font-mono">RN {ev.replacement_note_no}</span>}
                {ev.credit_note_no && <span className="text-indigo-600 dark:text-indigo-300 font-mono">CN {ev.credit_note_no}</span>}
                {ev.replacement_serial && <span className="font-mono">→ {ev.replacement_serial}</span>}
                {ev.notes && <span className="italic text-[var(--text-muted)]">{ev.notes}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Send to Supplier Modal */}
      <Modal isOpen={modal === 'send'} onClose={() => setModal('')} title="Send to Supplier" size="md">
        <div className="space-y-3">
          <p className="text-xs text-[var(--text-muted)]">
            Records that the faulty product was dispatched. The original serial will be marked
            <strong> With Supplier (RMA)</strong> so it cannot be sold or allocated elsewhere.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Supplier RMA No. (optional)" value={sendForm.supplier_rma_no}
              onChange={e => setSendForm({ ...sendForm, supplier_rma_no: e.target.value })}
              placeholder="e.g. RMA-5896" data-testid="send-supplier-rma-no" />
            <Input label="Sent Date *" type="date" value={sendForm.sent_date}
              onChange={e => setSendForm({ ...sendForm, sent_date: e.target.value })} data-testid="send-date" />
          </div>
          <Textarea label="Notes (optional)" rows={2} value={sendForm.notes}
            onChange={e => setSendForm({ ...sendForm, notes: e.target.value })} data-testid="send-notes" />
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <Button variant="secondary" onClick={() => setModal('')}>Cancel</Button>
            <Button onClick={submitSend} disabled={saving} data-testid="submit-send">
              {saving ? 'Saving…' : 'Confirm Send'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Receive Replacement Modal */}
      <Modal isOpen={modal === 'replace'} onClose={() => setModal('')} title="Receive Replacement" size="lg">
        <div className="space-y-3">
          <p className="text-xs text-[var(--text-muted)]">
            Records the supplier&apos;s replacement paperwork and adds the new serial to inventory.
            The original serial becomes <strong>Replaced by Supplier</strong>. For a customer RMA the new serial is <strong>Reserved for RMA</strong> — it stays in inventory but cannot be sold to anyone else until it&apos;s delivered.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Replacement Serial *" value={replaceForm.replacement_serial}
              onChange={e => setReplaceForm({ ...replaceForm, replacement_serial: e.target.value })}
              placeholder="Scan or enter the new serial" data-testid="replace-serial" />
            <Select label="Replacement Product (from inventory)" value={replaceForm.replacement_product_id}
              onChange={e => {
                const p = products?.find(x => x.id === e.target.value);
                setReplaceForm({
                  ...replaceForm,
                  replacement_product_id: e.target.value,
                  replacement_product_name: p?.name || '',
                  replacement_brand: p?.brand || '',
                  replacement_model: p?.model || '',
                });
              }}
              data-testid="replace-product">
              <option value="">— Or enter a new product below —</option>
              {(products || []).map(p => <option key={p.id} value={p.id}>{`${p.name}${p.brand ? ` · ${p.brand}` : ''}`}</option>)}
            </Select>
          </div>

          {/* Manual product entry — active when Upgraded checked OR no product selected */}
          {(replaceForm.force_upgraded || !replaceForm.replacement_product_id) && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3" data-testid="manual-product-entry">
              <div className="text-[11px] font-semibold text-blue-900 dark:text-blue-200 mb-2">
                {replaceForm.force_upgraded
                  ? 'Upgraded Replacement — enter the new product details'
                  : 'New replacement product — enter details below'}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input label="Replacement Product Name *" value={replaceForm.replacement_product_name}
                  onChange={e => setReplaceForm({ ...replaceForm, replacement_product_name: e.target.value })}
                  placeholder="e.g. Samsung 980 PRO 1TB" data-testid="replace-product-name" />
                <Input label="Brand" value={replaceForm.replacement_brand}
                  onChange={e => setReplaceForm({ ...replaceForm, replacement_brand: e.target.value })}
                  placeholder="e.g. Samsung" data-testid="replace-brand" />
                <Input label="Model" value={replaceForm.replacement_model}
                  onChange={e => setReplaceForm({ ...replaceForm, replacement_model: e.target.value })}
                  placeholder="e.g. MZ-V8P1T0" data-testid="replace-model" />
              </div>
              <p className="text-[10px] text-blue-700 dark:text-blue-300 mt-2">
                A product record will be created automatically if this exact name/brand/model doesn&apos;t exist yet.
              </p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <Input label="Supplier RMA No." value={replaceForm.supplier_rma_no}
              onChange={e => setReplaceForm({ ...replaceForm, supplier_rma_no: e.target.value })}
              placeholder="RMA-5896" data-testid="replace-supplier-rma-no" />
            <Input label="Delivery Note No." value={replaceForm.delivery_note_no}
              onChange={e => setReplaceForm({ ...replaceForm, delivery_note_no: e.target.value })}
              placeholder="DN-4512" data-testid="replace-delivery-note-no" />
            <Input label="Replacement Note No." value={replaceForm.replacement_note_no}
              onChange={e => setReplaceForm({ ...replaceForm, replacement_note_no: e.target.value })}
              placeholder="RN-1235" data-testid="replace-replacement-note-no" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Received Date *" type="date" value={replaceForm.received_date}
              onChange={e => setReplaceForm({ ...replaceForm, received_date: e.target.value })} data-testid="replace-received-date" />
            <Input label="Document Date" type="date" value={replaceForm.replacement_doc_date}
              onChange={e => setReplaceForm({ ...replaceForm, replacement_doc_date: e.target.value })}
              data-testid="replace-doc-date" />
          </div>
          <label className="flex items-center gap-2 text-sm" data-testid="replace-upgraded-toggle">
            <input type="checkbox" checked={replaceForm.force_upgraded}
              onChange={e => setReplaceForm({ ...replaceForm, force_upgraded: e.target.checked })} />
            Upgraded Replacement (different model)
          </label>
          <Textarea label="Remarks (from supplier document)" rows={2} value={replaceForm.replacement_remarks}
            onChange={e => setReplaceForm({ ...replaceForm, replacement_remarks: e.target.value })}
            data-testid="replace-remarks" />
          <Textarea label="Internal Notes" rows={2} value={replaceForm.notes}
            onChange={e => setReplaceForm({ ...replaceForm, notes: e.target.value })} data-testid="replace-notes" />
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <Button variant="secondary" onClick={() => setModal('')}>Cancel</Button>
            <Button onClick={submitReplace} disabled={saving} data-testid="submit-replace">
              {saving ? 'Saving…' : 'Record Replacement'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Receive Credit Modal */}
      <Modal isOpen={modal === 'credit'} onClose={() => setModal('')} title="Receive Credit (no replacement)" size="md">
        <div className="space-y-3">
          <p className="text-xs text-[var(--text-muted)]">
            The supplier is issuing a credit instead of replacing the product.
            The original serial becomes <strong>Credited by Supplier</strong> — no replacement will be added.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Credit Note No. *" value={creditForm.credit_note_no}
              onChange={e => setCreditForm({ ...creditForm, credit_note_no: e.target.value })}
              placeholder="e.g. CN-4588" data-testid="credit-note-no" />
            <Input label="Supplier RMA No." value={creditForm.supplier_rma_no}
              onChange={e => setCreditForm({ ...creditForm, supplier_rma_no: e.target.value })}
              placeholder="RMA-5896" data-testid="credit-supplier-rma-no" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Received Date *" type="date" value={creditForm.received_date}
              onChange={e => setCreditForm({ ...creditForm, received_date: e.target.value })} data-testid="credit-received-date" />
            <Input label="Document Date" type="date" value={creditForm.replacement_doc_date}
              onChange={e => setCreditForm({ ...creditForm, replacement_doc_date: e.target.value })}
              data-testid="credit-doc-date" />
          </div>
          <Textarea label="Remarks (from supplier document)" rows={2} value={creditForm.replacement_remarks}
            onChange={e => setCreditForm({ ...creditForm, replacement_remarks: e.target.value })}
            data-testid="credit-remarks" />
          <Textarea label="Internal Notes" rows={2} value={creditForm.notes}
            onChange={e => setCreditForm({ ...creditForm, notes: e.target.value })} data-testid="credit-notes" />
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <Button variant="secondary" onClick={() => setModal('')}>Cancel</Button>
            <Button onClick={submitCredit} disabled={saving} data-testid="submit-credit">
              {saving ? 'Saving…' : 'Record Credit'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Received Repaired (Same Serial) Modal */}
      <Modal isOpen={modal === 'repaired'} onClose={() => setModal('')} title="Mark Repaired (Same Serial Returned)" size="md">
        <div className="space-y-3">
          <p className="text-xs text-[var(--text-muted)]">
            Supplier repaired the item and returned the <strong>same serial</strong>. Serial goes back to
            {rma.rma_type === 'Internal' ? ' In Stock' : ' Allocated to Repair Ticket'}. No new serial is added.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Supplier RMA No." value={repairedForm.supplier_rma_no}
              onChange={e => setRepairedForm({ ...repairedForm, supplier_rma_no: e.target.value })}
              placeholder="RMA-5896" data-testid="repaired-supplier-rma-no" />
            <Input label="Repair/Delivery Note No." value={repairedForm.replacement_note_no}
              onChange={e => setRepairedForm({ ...repairedForm, replacement_note_no: e.target.value })}
              placeholder="REP-2026-001245" data-testid="repaired-doc-no" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Received Date *" type="date" value={repairedForm.received_date}
              onChange={e => setRepairedForm({ ...repairedForm, received_date: e.target.value })} data-testid="repaired-received-date" />
            <Input label="Document Date" type="date" value={repairedForm.replacement_doc_date}
              onChange={e => setRepairedForm({ ...repairedForm, replacement_doc_date: e.target.value })}
              data-testid="repaired-doc-date" />
          </div>
          <Textarea label="Remarks (from supplier document)" rows={2} value={repairedForm.replacement_remarks}
            onChange={e => setRepairedForm({ ...repairedForm, replacement_remarks: e.target.value })}
            data-testid="repaired-remarks" />
          <Textarea label="Internal Notes" rows={2} value={repairedForm.notes}
            onChange={e => setRepairedForm({ ...repairedForm, notes: e.target.value })} data-testid="repaired-notes" />
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <Button variant="secondary" onClick={() => setModal('')}>Cancel</Button>
            <Button onClick={submitRepaired} disabled={saving} data-testid="submit-repaired">
              {saving ? 'Saving…' : 'Confirm Repaired'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Warranty Rejected Modal */}
      <Modal isOpen={modal === 'reject'} onClose={() => setModal('')} title="Reject Warranty Claim" size="md">
        <div className="space-y-3">
          <p className="text-xs text-[var(--text-muted)]">
            Supplier rejected the warranty claim. Original serial will be marked <strong>Defective</strong>.
            No replacement or credit is recorded.
          </p>
          <Textarea label="Rejection Reason *" rows={3} value={rejectForm.rejection_reason}
            onChange={e => setRejectForm({ ...rejectForm, rejection_reason: e.target.value })}
            placeholder="e.g. Physical damage void warranty" data-testid="reject-reason" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Supplier RMA No." value={rejectForm.supplier_rma_no}
              onChange={e => setRejectForm({ ...rejectForm, supplier_rma_no: e.target.value })}
              placeholder="RMA-5896" data-testid="reject-supplier-rma-no" />
            <Input label="Received Date *" type="date" value={rejectForm.received_date}
              onChange={e => setRejectForm({ ...rejectForm, received_date: e.target.value })} data-testid="reject-received-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Document Date" type="date" value={rejectForm.replacement_doc_date}
              onChange={e => setRejectForm({ ...rejectForm, replacement_doc_date: e.target.value })}
              data-testid="reject-doc-date" />
            <Input label="Document Remarks" value={rejectForm.replacement_remarks}
              onChange={e => setRejectForm({ ...rejectForm, replacement_remarks: e.target.value })}
              data-testid="reject-remarks" />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <Button variant="secondary" onClick={() => setModal('')}>Cancel</Button>
            <Button onClick={submitReject} disabled={saving} data-testid="submit-reject">
              {saving ? 'Saving…' : 'Confirm Rejection'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Deliver to Customer Modal */}
      <Modal isOpen={modal === 'deliver'} onClose={() => setModal('')} title="Deliver to Customer &amp; Close RMA" size="md">
        <div className="space-y-3">
          <p className="text-xs text-[var(--text-muted)]">
            Marks the replacement serial <strong>{rma.serial_replacement || '(none set)'}</strong> as Sold,
            closes any linked repair ticket, and sets the RMA status to <strong>Closed</strong>.
          </p>
          <Textarea label="Notes" rows={2} value={deliverForm.notes}
            onChange={e => setDeliverForm({ ...deliverForm, notes: e.target.value })} data-testid="deliver-notes" />
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <Button variant="secondary" onClick={() => setModal('')}>Cancel</Button>
            <Button onClick={submitDeliver} disabled={saving} data-testid="submit-deliver">
              {saving ? 'Saving…' : 'Confirm Delivery'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const RefField = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
    <div className={`text-sm font-semibold ${mono ? 'font-mono' : ''}`}>{value || <span className="text-gray-400 italic font-normal">—</span>}</div>
  </div>
);

export default SupplierRMAWorkflow;
